// components/audio/BatchAudioGenerator.js
import React, { useEffect, useState } from 'react';
import { Bus, Volume2, Play, Loader, CheckCircle, AlertCircle, Download, Layers, ChevronDown, ChevronUp } from 'lucide-react';
import { AVAILABLE_VOICES, VOICE_GROUPS, findVoiceForLanguage } from '../ItineraryCreator/utils/voiceConstants';
import { LANGUAGES } from '../selectors/LanguageSelector';
import { useSimpleAudioGeneration } from '../hooks/useSimpleAudioGeneration';
import { convertToSSML } from '../ItineraryCreator/utils/ssmlUtils';
import { AUDIO_COMPRESSION_PRESETS, compressAndUploadAudioVariant } from '../ItineraryCreator/utils/audioCompression';
import { calculateTTSCost } from '../ItineraryCreator/utils/ttsPricing';
import { buildAudioGenerationStamp, getPoiAudioGenerationInfo } from './audioGenerationInfo';

const getDefaultChunkSizeForVoice = (voiceId) => {
  if (!voiceId) return 800;
  // Gemini 3.8 stays stable on long audio, so only very long texts get split
  if (voiceId.includes('SimpleTTS-38')) return 5000;
  if (voiceId.includes('Erinome')) return 600;
  if (voiceId.includes('SimpleTTS-Legacy') || voiceId.includes('SimpleTTS-Pro')) return 1000;
  if (voiceId.includes('SimpleTTS')) return 800;
  return 800;
};

const ERINOME_CITYRAMA_STYLE_PREFIX = '[high-pitched], [playful], [excited]';
const MIN_AUDIO_CHUNK_SIZE = 100;

const applyErinomeCityramaPrefix = (text) => {
  const normalizedText = String(text || '').trim();
  if (!normalizedText) return ERINOME_CITYRAMA_STYLE_PREFIX;
  if (normalizedText.startsWith(ERINOME_CITYRAMA_STYLE_PREFIX)) {
    return normalizedText;
  }
  return `${ERINOME_CITYRAMA_STYLE_PREFIX} ${normalizedText}`;
};

const resolveAudioChunkSize = (value, fallback = 800) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(MIN_AUDIO_CHUNK_SIZE, Math.floor(parsed));
};

const splitAudioTextIntoChunks = (text, maxChunkSize = 800) => {
  const normalized = String(text || '').replace(/\r\n/g, '\n').trim();
  if (!normalized || normalized.length <= maxChunkSize) {
    return normalized ? [normalized] : [];
  }

  const paragraphs = normalized.split('\n\n').map((part) => part.trim()).filter(Boolean);
  if (paragraphs.length <= 1) {
    const chunks = [];
    let remaining = normalized;
    while (remaining.length > maxChunkSize) {
      let splitPoint = remaining.lastIndexOf(' ', maxChunkSize);
      if (splitPoint < Math.floor(maxChunkSize * 0.6)) {
        splitPoint = maxChunkSize;
      }
      chunks.push(remaining.slice(0, splitPoint).trim());
      remaining = remaining.slice(splitPoint).trim();
    }
    if (remaining) chunks.push(remaining);
    return chunks;
  }

  const chunks = [];
  let currentChunk = '';
  paragraphs.forEach((paragraph) => {
    const candidate = currentChunk ? `${currentChunk}\n\n${paragraph}` : paragraph;
    if (currentChunk && candidate.length > maxChunkSize) {
      chunks.push(currentChunk);
      currentChunk = paragraph;
    } else {
      currentChunk = candidate;
    }
  });
  if (currentChunk) chunks.push(currentChunk);
  return chunks;
};

// Resolve the guide's language (e.g. 'pt' or 'pt-PT') to a code in the language dropdown
const resolveGuideLanguage = (language) => {
  const normalized = String(language || '').trim().replace('_', '-').toLowerCase();
  const exactMatch = LANGUAGES.find((lang) => lang.code.toLowerCase() === normalized);
  if (exactMatch) return exactMatch.code;
  const voiceLanguage = AVAILABLE_VOICES[findVoiceForLanguage(normalized)]?.languageCode;
  return LANGUAGES.find((lang) => lang.code === voiceLanguage)?.code || 'en-US';
};

const getPoiKey = (poi) => String(poi.id ?? poi.name ?? '');

const formatGeneratedAt = (date) => date.toLocaleString(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
});

const BatchAudioGenerator = ({
  itinerary,
  onBatchComplete,
  onIndividualComplete
}) => {
  const guideLanguage = resolveGuideLanguage(itinerary?.metadata?.language);
  const [selectedLanguage, setSelectedLanguage] = useState(guideLanguage);
  const [selectedVoice, setSelectedVoice] = useState(() => findVoiceForLanguage(guideLanguage));
  const [speechRate, setSpeechRate] = useState('1');
  const [chunkingEnabled, setChunkingEnabled] = useState(true);
  const [chunkSize, setChunkSize] = useState(() => getDefaultChunkSizeForVoice(findVoiceForLanguage(guideLanguage)));
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState([]);
  const [generationStats, setGenerationStats] = useState(null);
  const [isConverting48k, setIsConverting48k] = useState(false);
  const [conversionProgress, setConversionProgress] = useState([]);
  const [conversionStats, setConversionStats] = useState(null);

  // Gemini Voice Style Controls - Defaults: Sarcastic Tour Guide preset
  const [geminiTone, setGeminiTone] = useState(55); // 0-100: calm to excited (55 = sarcastic preset)
  const [geminiPace, setGeminiPace] = useState(70); // 0-100: slow to fast (70 = sarcastic preset)
  const [geminiFormality, setGeminiFormality] = useState(25); // 0-100: casual to formal (25 = casual)
  const [geminiTemperature, setGeminiTemperature] = useState(1.55); // 0.0-2.0: deterministic to creative (1.55 = sweet spot)
  const [geminiCustomPrompt, setGeminiCustomPrompt] = useState('You are Sofia, a witty and slightly irreverent tour guide who has seen it all. Deliver the content with dry humor and occasional gentle sarcasm, as if sharing secrets with a friend at a cafe. Use playful skepticism when mentioning historical legends. Maintain a conversational, unhurried pace with dramatic pauses before punchlines. Your tone should be warm but knowingly amused.');
  const [personalityPreset, setPersonalityPreset] = useState('sarcastic'); // Personality preset selection (default: sarcastic)
  const [isChunkPreviewExpanded, setIsChunkPreviewExpanded] = useState(false);
  const [previewPoiName, setPreviewPoiName] = useState('');
  const [isStopPickerOpen, setIsStopPickerOpen] = useState(false);
  const [selectedPoiKeys, setSelectedPoiKeys] = useState([]);

  const { generateAudio, isGenerating: hookIsGenerating, error: hookError, chunkProgress } = useSimpleAudioGeneration();
  const defaultCompressionPreset = AUDIO_COMPRESSION_PRESETS.find(p => p.id === 'mp3-48k') || AUDIO_COMPRESSION_PRESETS[0];

  const hasExistingAudio = (poi) => Boolean(
    poi?.audioUrl
    || poi?.uncompressedAudioUrl
    || poi?.originalAudioUrl
  );

  // Only generate for POIs that have source text and do not already have audio.
  const validPois = itinerary.points?.filter((poi) =>
    poi.inDepthDescription
    && poi.inDepthDescription.trim().length > 0
    && !hasExistingAudio(poi)
  ) || [];

  const describedPois = itinerary.points?.filter((poi) =>
    poi.inDepthDescription
    && poi.inDepthDescription.trim().length > 0
  ) || [];

  const selectedPois = describedPois.filter((poi) => selectedPoiKeys.includes(getPoiKey(poi)));

  const togglePoiSelection = (poiKey) => {
    setSelectedPoiKeys((prev) => (
      prev.includes(poiKey) ? prev.filter((key) => key !== poiKey) : [...prev, poiKey]
    ));
  };

  const selectStopsNotOnLatestModel = () => {
    setSelectedPoiKeys(describedPois
      .filter((poi) => !getPoiAudioGenerationInfo(poi).isLatestModel)
      .map(getPoiKey));
  };

  const totalDescriptionCharacters = describedPois.reduce(
    (sum, poi) => sum + String(poi.inDepthDescription || '').trim().length,
    0
  );

  const batchCostEstimate = calculateTTSCost(selectedVoice, totalDescriptionCharacters);
  const batchCostFormatted = batchCostEstimate.error
    ? null
    : (batchCostEstimate.cost < 0.01
      ? `$${batchCostEstimate.cost.toFixed(6)}`
      : `$${batchCostEstimate.cost.toFixed(4)}`);

  useEffect(() => {
    if (!describedPois.length) {
      setPreviewPoiName('');
      return;
    }

    const hasCurrentPreviewPoi = describedPois.some((poi) => poi.name === previewPoiName);
    if (!hasCurrentPreviewPoi) {
      setPreviewPoiName(describedPois[0].name || '');
    }
  }, [describedPois, previewPoiName]);

  const audioPois = itinerary.points?.filter(poi => poi.audioUrl) || [];

  // Check if selected voice is a Gemini voice
  const isGeminiVoice = () => {
    return selectedVoice && selectedVoice.includes('Gemini');
  };

  // Personality presets - all with conversational style
  const personalityPresets = {
    professional: {
      name: '🎯 Professional Tour Guide',
      tone: 50,
      pace: 60,
      formality: 25,
      prompt: 'You are Sofia, a knowledgeable and articulate tour guide. Speak in a warm, professional tone as if personally guiding a small group through a fascinating location. Maintain a steady, measured pace with natural pauses between key points. Your delivery should feel like a trusted expert sharing insights with curious adults. Emphasize important historical facts with subtle gravitas.'
    },
    sarcastic: {
      name: '🎭 Sarcastic Tour Guide',
      tone: 55,
      pace: 70,
      formality: 25,
      prompt: 'You are Sofia, a witty and slightly irreverent tour guide who has seen it all. Deliver the content with dry humor and occasional gentle sarcasm, as if sharing secrets with a friend at a cafe. Use playful skepticism when mentioning historical legends. Maintain a conversational, unhurried pace with dramatic pauses before punchlines. Your tone should be warm but knowingly amused.'
    },
    enthusiastic: {
      name: '🌟 Enthusiastic Guide',
      tone: 75,
      pace: 70,
      formality: 25,
      prompt: 'You are Sofia, a passionate and energetic tour guide who genuinely loves every location. Speak with infectious enthusiasm and wonder, as if experiencing each sight for the first time alongside your guests. Let your excitement build naturally when revealing surprising facts. Use dynamic pacing - quickening slightly for exciting moments, slowing for dramatic revelations. Your joy should be palpable but genuine, never forced.'
    },
    storyteller: {
      name: '🎪 Storyteller',
      tone: 65,
      pace: 55,
      formality: 25,
      prompt: 'You are Sofia, a masterful storyteller and tour guide who weaves history into captivating narratives. Speak as if sharing ancient secrets by firelight - with drama, mystery, and a sense of wonder. Vary your pacing dramatically: slow and deliberate for suspense, quicker for action. Use theatrical pauses before revelations. Paint vivid scenes with your voice, making listeners feel transported through time.'
    },
    educational: {
      name: '🎓 Educational Narrator',
      tone: 50,
      pace: 50,
      formality: 25,
      prompt: 'You are Sofia, a scholarly guide with deep expertise. Deliver content with clarity and authority, as if lecturing to engaged university students on a field trip. Speak at a measured, deliberate pace that allows complex information to be absorbed. Emphasize key dates, names, and concepts with subtle vocal weight. Maintain intellectual warmth while ensuring every detail is precisely communicated.'
    },
    custom: {
      name: '⚙️ Custom',
      tone: 50,
      pace: 65,
      formality: 25,
      prompt: ''
    },
    pt_pt: {
      name: '🇵🇹 European Portuguese (PT)',
      tone: 55,
      pace: 65,
      formality: 25,
      prompt: 'You are Sofia, a knowledgeable tour guide from Portugal. Speak in European Portuguese from Portugal with proper European Portuguese pronunciation.  Deliver with a warm, professional tone as if guiding visitors through a fascinating Portuguese location. Use natural pauses between key points.'
    },
    pt_br: {
      name: '🇧🇷 Brazilian Portuguese (BR)',
      tone: 55,
      pace: 65,
      formality: 25,
      prompt: 'You are Sofia, a knowledgeable tour guide from Brazil. Speak in authentic Brazilian Portuguese from Brazil with proper Brazilian pronunciation and accent. Deliver with a warm, professional tone as if guiding visitors through a fascinating Brazilian location. Use natural pauses between key points.'
    }
  };

  // Handle personality preset change
  const handlePersonalityPresetChange = (presetKey) => {
    setPersonalityPreset(presetKey);
    const preset = personalityPresets[presetKey];
    if (preset) {
      setGeminiTone(preset.tone);
      setGeminiPace(preset.pace);
      setGeminiFormality(preset.formality);
      setGeminiCustomPrompt(preset.prompt);
    }
  };

  // Build Gemini style prompt from sliders and custom input
  const buildGeminiStylePrompt = () => {
    if (!isGeminiVoice()) return '';

    const parts = [];

    // Tone (calm to excited)
    if (geminiTone <= 30) {
      parts.push('in a calm and soothing tone');
    } else if (geminiTone >= 70) {
      parts.push('with excitement and enthusiasm');
    } else if (geminiTone >= 45 && geminiTone <= 55) {
      parts.push('in a neutral, professional tone');
    }

    // Pace (slow to fast) - More granular control
    if (geminiPace <= 20) {
      parts.push('speaking very slowly and deliberately');
    } else if (geminiPace <= 40) {
      parts.push('speaking at a slow, relaxed pace');
    } else if (geminiPace >= 80) {
      parts.push('at a very brisk, energetic pace');
    } else if (geminiPace >= 60) {
      parts.push('at a moderately fast, lively pace');
    }
    // 41-59: No pace instruction (natural/normal speed)

    // Formality (casual to formal)
    if (geminiFormality <= 30) {
      parts.push('in a casual, conversational style');
    } else if (geminiFormality >= 70) {
      parts.push('with a formal, authoritative delivery');
    }

    // Combine slider-based prompt
    let prompt = parts.length > 0 ? `Narrate ${parts.join(', ')}.` : '';

    // Add custom prompt if provided
    if (geminiCustomPrompt.trim()) {
      prompt = prompt ? `${prompt} ${geminiCustomPrompt.trim()}` : geminiCustomPrompt.trim();
    }

    // CRITICAL: Auto-inject language-specific pronunciation instructions based on selected language
    const languageGuidanceMap = {
      'pt-PT': 'You MUST speak in EUROPEAN PORTUGUESE from PORTUGAL with proper European Portuguese pronunciation. NOT Brazilian Portuguese.',
      'pt-BR': 'Speak in authentic BRAZILIAN PORTUGUESE from BRAZIL with proper Brazilian pronunciation and accent.',
      'es-ES': 'Speak in CASTILIAN SPANISH from SPAIN with proper Castilian pronunciation.',
      'es-MX': 'Speak in MEXICAN SPANISH with proper Mexican accent.',
      'en-US': 'Speak in AMERICAN ENGLISH.',
      'en-GB': 'Speak in BRITISH ENGLISH.',
      'fr-FR': 'Speak in FRENCH from FRANCE.'
    };

    const languageGuidance = languageGuidanceMap[selectedLanguage] || '';

    // Prepend language guidance FIRST so it takes priority
    if (languageGuidance) {
      prompt = languageGuidance + (prompt ? ` ${prompt}` : '');
    }

    return prompt;
  };

  const batchChunkPreview = (() => {
    const previewPoi = describedPois.find((poi) => poi.name === previewPoiName) || describedPois[0];
    if (!previewPoi?.inDepthDescription || !selectedVoice) {
      return null;
    }

    const isErinomeVoice = selectedVoice.includes('Erinome');
    const isGeminiSelectedVoice = selectedVoice.includes('Gemini');
    const useSSML = !isGeminiSelectedVoice && (previewPoi.useSSML !== undefined ? previewPoi.useSSML : true);
    const initialText = useSSML
      ? convertToSSML(previewPoi.inDepthDescription, selectedLanguage)
      : previewPoi.inDepthDescription;
    let previewText = initialText;

    if (isGeminiSelectedVoice) {
      previewText = previewText
        .replace(/<speak>/gi, '')
        .replace(/<\/speak>/gi, '')
        .replace(/<break\s+time="0\.85s"\s*\/>/gi, '\n\n')
        .replace(/<break[^>]*\/>/gi, ' ')
        .replace(/<break[^>]*><\/break>/gi, ' ')
        .replace(/<\/?(prosody|voice|emphasis|say-as|phoneme|sub|lang|p|s)[^>]*>/gi, '')
        .replace(/\r\n/g, '\n')
        .replace(/ +/g, ' ')
        .trim();
    }

    if (isErinomeVoice) {
      previewText = applyErinomeCityramaPrefix(previewText);
    }

    const resolvedPreviewChunkSize = resolveAudioChunkSize(chunkSize, getDefaultChunkSizeForVoice(selectedVoice));
    const shouldChunkPreview = chunkingEnabled && previewText.length > resolvedPreviewChunkSize;
    const previewChunks = shouldChunkPreview
      ? splitAudioTextIntoChunks(previewText, resolvedPreviewChunkSize).map((chunk) => (
          isErinomeVoice ? applyErinomeCityramaPrefix(chunk) : chunk
        ))
      : [previewText];

    return {
      poiName: previewPoi.name || 'Unnamed POI',
      useSSML,
      geminiPrompt: buildGeminiStylePrompt(),
      finalText: previewText,
      chunkSize: resolvedPreviewChunkSize,
      shouldChunk: shouldChunkPreview,
      chunks: previewChunks.filter(Boolean),
    };
  })();

  // Get default speech rate based on voice type
  const getDefaultSpeechRate = (voiceId) => {
    if (!voiceId) return '1.0';

    // Gemini voices work better with faster speech rate
    if (voiceId.includes('Gemini')) {
      return '1.15';
    }

    // Chirp3 voices also benefit from slightly faster rate
    if (voiceId.includes('Chirp3')) {
      return '1.1';
    }

    // Azure voices
    if (voiceId.includes('Neural') || voiceId.includes('Multilingual')) {
      return '1.0';
    }

    // Default for all other voices
    return '1.0';
  };

  // Update voice when language changes
  const handleLanguageChange = (newLanguage) => {
    setSelectedLanguage(newLanguage);
    const recommendedVoice = findVoiceForLanguage(newLanguage);
    setSelectedVoice(recommendedVoice);
    // Update speech rate to match the recommended voice
    setSpeechRate(getDefaultSpeechRate(recommendedVoice));
    setChunkSize(getDefaultChunkSizeForVoice(recommendedVoice));
  };

  // Follow the guide's language when it loads or changes, so a pt-PT guide isn't voiced with English settings
  useEffect(() => {
    if (!isGenerating) handleLanguageChange(guideLanguage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guideLanguage]);

  // Handle voice selection change
  const handleVoiceChange = (newVoice) => {
    setSelectedVoice(newVoice);
    // Update speech rate to match the selected voice
    setSpeechRate(getDefaultSpeechRate(newVoice));
    setChunkSize(getDefaultChunkSizeForVoice(newVoice));
  };

  useEffect(() => {
    setChunkSize(getDefaultChunkSizeForVoice(selectedVoice));
  }, [selectedVoice]);

  // Add progress message
  const addProgress = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setProgress(prev => [...prev, { 
      message, 
      type, 
      timestamp,
      id: Date.now() + Math.random() 
    }]);
  };

  // Clear progress
  const clearProgress = () => {
    setProgress([]);
    setGenerationStats(null);
    setConversionProgress([]);
    setConversionStats(null);
  };

  const addConversionProgress = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setConversionProgress(prev => [...prev, {
      message,
      type,
      timestamp,
      id: Date.now() + Math.random()
    }]);
  };

  // Main batch generation function
  const handleBatchGeneration = async ({ forceRegenerate = false, pois = null } = {}) => {
    // An explicit stop selection always regenerates, even stops that already have audio
    const isSelection = Array.isArray(pois);
    const poisToProcess = isSelection ? pois : (forceRegenerate ? describedPois : validPois);
    forceRegenerate = forceRegenerate || isSelection;

    if (poisToProcess.length === 0) {
      addProgress(
        isSelection
          ? 'No stops selected.'
          : forceRegenerate
            ? 'No POIs with in-depth descriptions found.'
            : 'No POIs are missing audio. Batch generation only runs for stops without audio.',
        'error'
      );
      return;
    }

    setIsGenerating(true);
    clearProgress();
    
    const startTime = Date.now();
    let successCount = 0;
    let errorCount = 0;
    const results = [];

    addProgress(
      forceRegenerate
        ? `Re-generating audio for ${poisToProcess.length} POIs with descriptions`
        : `Starting batch audio generation for ${poisToProcess.length} POIs missing audio`,
      'info'
    );
    addProgress(`Language: ${getLanguageName(selectedLanguage)}, Voice: ${getVoiceName(selectedVoice)}`, 'info');

    try {
      for (let i = 0; i < poisToProcess.length; i++) {
        const poi = poisToProcess[i];
        const poiNumber = i + 1;
        
        addProgress(
          `[${poiNumber}/${poisToProcess.length}] ${forceRegenerate ? 'Re-generating' : 'Generating'} audio for "${poi.name}"...`,
          'processing'
        );

        try {
          // Prepare the text for TTS.
          // Gemini/SimpleTTS voices don't support SSML and convertToSSML replaces \n\n with
          // <break> tags — which destroys paragraph structure needed for chunking.
          // Skip SSML conversion entirely for Gemini voices and send raw text.
          const isGemini = selectedVoice && selectedVoice.includes('Gemini');
          const useSSML = !isGemini && (poi.useSSML !== undefined ? poi.useSSML : true);
          const finalText = useSSML ? convertToSSML(poi.inDepthDescription, selectedLanguage) : poi.inDepthDescription;

          // Build Gemini style prompt if using Gemini voice
          const geminiPrompt = buildGeminiStylePrompt();
          if (geminiPrompt) {
            console.log(`[BatchAudioGenerator] Using Gemini style prompt for ${poi.name}:`, geminiPrompt);
          }

          // Generate audio using the hook with chunk progress callback
          const result = await generateAudio(
            finalText,
            poi,
            {
              ...itinerary.metadata,
              language: selectedLanguage // Override language for this generation
            },
            selectedVoice,
            speechRate,
            {
              geminiPrompt,
              temperature: geminiTemperature,
              chunkingEnabled,
              chunkSize,
              onChunkProgress: (chunkInfo) => {
                // Display chunk progress in the log
                if (chunkInfo.status === 'starting') {
                  addProgress(`🔀 "${poi.name}" requires chunking: ${chunkInfo.total} chunks to process`, 'info');
                } else if (chunkInfo.status === 'processing') {
                  addProgress(`   ↳ Processing chunk ${chunkInfo.current}/${chunkInfo.total}...`, 'processing');
                } else if (chunkInfo.status === 'completed') {
                  addProgress(`   ↳ All ${chunkInfo.total} chunks merged successfully`, 'success');
                } else if (chunkInfo.status === 'error') {
                  addProgress(`   ↳ Chunk processing failed: ${chunkInfo.error}`, 'error');
                }
              }
            }
          );

          if (result && result.audioUrl) {
            let updatedPoi = {
              ...poi,
              audioUrl: result.audioUrl,
              ttsVoice: selectedVoice,
              ...buildAudioGenerationStamp(selectedVoice, result),
              language: selectedLanguage,
              estimatedDuration: result.audioDuration && result.audioDuration > 0
                ? Math.ceil(result.audioDuration / 60)
                : poi.estimatedDuration
            };

            // The TTS function already stores a 48 kbps file; compressing it again would only re-encode it
            const alreadyCompressed = String(result.audioUrl).includes('/compressed/');

            if (alreadyCompressed) {
              updatedPoi = {
                ...updatedPoi,
                uncompressedAudioUrl: result.audioUrl,
                originalAudioUrl: result.audioUrl,
                compressedAudioVariants: []
              };
            } else {
              try {
                const compressedVariant = await compressAndUploadAudioVariant({
                  audioUrl: result.audioUrl,
                  sourceAudioUrl: result.audioUrl,
                  preset: defaultCompressionPreset,
                });

                updatedPoi = {
                  ...updatedPoi,
                  audioUrl: compressedVariant.audioUrl,
                  uncompressedAudioUrl: compressedVariant.originalAudioUrl,
                  originalAudioUrl: compressedVariant.originalAudioUrl,
                  compressedAudioVariants: [{
                    ...compressedVariant,
                    sourceAudioUrl: compressedVariant.originalAudioUrl || result.audioUrl,
                    compressedAt: new Date().toISOString(),
                  }]
                };

                addProgress(`   ↳ Compressed "${poi.name}" to 48 kbps`, 'success');
              } catch (compressionError) {
                console.warn(`[BatchAudioGenerator] 48k compression failed for ${poi.name}; using original audio`, compressionError);
                updatedPoi = {
                  ...updatedPoi,
                  uncompressedAudioUrl: result.audioUrl,
                  originalAudioUrl: result.audioUrl,
                  compressedAudioVariants: []
                };
                addProgress(`   ↳ 48k compression failed; kept original audio for "${poi.name}"`, 'error');
              }
            }

            console.log('🎵 BatchAudioGenerator: Created updatedPoi with audioUrl:', {
              poiId: updatedPoi.id,
              poiName: updatedPoi.name,
              audioUrl: updatedPoi.audioUrl,
              hasAudioUrl: !!updatedPoi.audioUrl,
              audioUrlLength: updatedPoi.audioUrl?.length
            });

            results.push(updatedPoi);
            successCount++;

            addProgress(`✓ Audio generated for "${poi.name}"`, 'success');

            // Call individual completion callback if provided
            if (onIndividualComplete) {
              console.log('🎵 BatchAudioGenerator: Calling onIndividualComplete with updatedPoi:', {
                id: updatedPoi.id,
                name: updatedPoi.name,
                hasAudioUrl: !!updatedPoi.audioUrl
              });
              onIndividualComplete(updatedPoi, poiNumber, poisToProcess.length);
            }

          } else {
            throw new Error('No audio URL received from generation');
          }

        } catch (error) {
          errorCount++;
          addProgress(`✗ Failed to generate audio for "${poi.name}": ${error.message}`, 'error');
          console.error(`Audio generation failed for POI ${poi.name}:`, error);
          
          // Still add the original POI to results to avoid data loss
          results.push(poi);
        }

        // Small delay between generations to avoid overwhelming the API
        if (i < poisToProcess.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      // Calculate final statistics
      const endTime = Date.now();
      const totalTime = Math.round((endTime - startTime) / 1000);
      const stats = {
        total: poisToProcess.length,
        successful: successCount,
        failed: errorCount,
        totalTime,
        averageTime: Math.round(totalTime / poisToProcess.length)
      };

      setGenerationStats(stats);
      
      if (successCount > 0) {
        addProgress(`🎉 Batch generation complete! ${successCount}/${poisToProcess.length} successful in ${totalTime}s`, 'success');
      } else {
      addProgress(`❌ Batch generation failed - no audio files were generated`, 'error');
      }

      // Call batch completion callback if provided
      if (onBatchComplete) {
        onBatchComplete(results, stats);
      }

    } catch (error) {
      addProgress(`❌ Batch generation failed: ${error.message}`, 'error');
      console.error('Batch audio generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleBatch48kConversion = async () => {
    if (audioPois.length === 0) {
      addConversionProgress('No POIs with audio were found', 'error');
      return;
    }

    setIsConverting48k(true);
    setConversionProgress([]);
    setConversionStats(null);

    const startTime = Date.now();
    const results = [];
    let successCount = 0;
    let errorCount = 0;

    addConversionProgress(`Starting 48k conversion for ${audioPois.length} POIs`, 'info');

    try {
      for (let i = 0; i < audioPois.length; i += 1) {
        const poi = audioPois[i];
        const poiNumber = i + 1;
        const sourceAudioUrl = poi.uncompressedAudioUrl || poi.originalAudioUrl || poi.audioUrl;

        if (!sourceAudioUrl) {
          errorCount += 1;
          addConversionProgress(`✗ No source audio URL for "${poi.name}"`, 'error');
          results.push(poi);
          continue;
        }

        addConversionProgress(`[${poiNumber}/${audioPois.length}] Converting "${poi.name}" to 48k...`, 'processing');

        try {
          const compressedVariant = await compressAndUploadAudioVariant({
            audioUrl: sourceAudioUrl,
            sourceAudioUrl,
            preset: defaultCompressionPreset,
          });

          const convertedPoi = {
            ...poi,
            audioUrl: compressedVariant.audioUrl,
            uncompressedAudioUrl: compressedVariant.originalAudioUrl,
            originalAudioUrl: compressedVariant.originalAudioUrl,
            compressedAudioVariants: [
              ...(Array.isArray(poi.compressedAudioVariants) ? poi.compressedAudioVariants.filter(item => item?.presetId !== defaultCompressionPreset.id) : []),
              {
                ...compressedVariant,
                sourceAudioUrl: compressedVariant.originalAudioUrl || sourceAudioUrl,
                compressedAt: new Date().toISOString(),
              }
            ],
          };

          results.push(convertedPoi);
          successCount += 1;
          addConversionProgress(`✓ Converted "${poi.name}" to 48k`, 'success');

          if (onIndividualComplete) {
            onIndividualComplete(convertedPoi, poiNumber, audioPois.length);
          }
        } catch (error) {
          errorCount += 1;
          addConversionProgress(`✗ Failed to convert "${poi.name}": ${error.message}`, 'error');
          console.error(`[BatchAudioGenerator] 48k conversion failed for ${poi.name}:`, error);
          results.push(poi);
        }

        if (i < audioPois.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 750));
        }
      }

      const totalTime = Math.round((Date.now() - startTime) / 1000);
      const stats = {
        total: audioPois.length,
        successful: successCount,
        failed: errorCount,
        totalTime,
        averageTime: Math.max(1, Math.round(totalTime / Math.max(1, audioPois.length))),
        mode: 'conversion',
        presetId: defaultCompressionPreset.id,
      };

      setConversionStats(stats);

      if (successCount > 0) {
        addConversionProgress(`🎉 48k conversion complete! ${successCount}/${audioPois.length} successful in ${totalTime}s`, 'success');
      } else {
        addConversionProgress('❌ 48k conversion failed - no audio files were converted', 'error');
      }

      if (onBatchComplete) {
        onBatchComplete(results, stats);
      }
    } catch (error) {
      addConversionProgress(`❌ 48k conversion failed: ${error.message}`, 'error');
      console.error('Batch 48k conversion error:', error);
    } finally {
      setIsConverting48k(false);
    }
  };

  // Helper functions
  const getLanguageName = (langCode) => {
    const language = LANGUAGES.find(lang => lang.code === langCode);
    return language ? language.name : langCode;
  };

  const getVoiceName = (voiceId) => {
    const voice = AVAILABLE_VOICES[voiceId];
    return voice ? voice.name : voiceId;
  };

  const getProgressIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'processing': return <Loader className="w-4 h-4 text-blue-500 animate-spin" />;
      default: return <Volume2 className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Volume2 className="w-5 h-5 text-blue-500" />
          Batch Audio Generation
        </h3>
        <span className="text-sm text-gray-500">
          {validPois.length} POIs missing audio
        </span>
      </div>

      <div className="mb-5 rounded-lg border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Batch Cost Estimate</p>
            <p className="mt-1 text-sm text-emerald-900">
              Estimated cost for all {describedPois.length} POIs with in-depth descriptions.
            </p>
            <p className="mt-1 text-xs text-emerald-700">
              Voice: {getVoiceName(selectedVoice)}
            </p>
          </div>
          <div className="text-right">
            {batchCostEstimate.error ? (
              <p className="text-sm font-medium text-amber-700">Pricing unavailable for selected voice</p>
            ) : (
              <>
                <p className="text-2xl font-bold text-emerald-800">{batchCostFormatted}</p>
                <p className="text-xs text-emerald-700">{totalDescriptionCharacters.toLocaleString()} characters</p>
              </>
            )}
          </div>
        </div>
      </div>

      {false && (
      <div className="mb-6 p-5 bg-amber-50 border-2 border-amber-300 rounded-xl shadow-sm">
        <div className="flex flex-col gap-4">
          <div>
            <h4 className="text-base font-semibold text-amber-950">Batch Audio 48k Conversion</h4>
            <p className="text-sm text-amber-900 mt-1 leading-5">
              Convert all existing MP3s in this itinerary to the default 48 kbps version.
              The original files stay in `uncompressed/`, and the JSON keeps the 48k path.
            </p>
            <p className="text-xs text-amber-800 mt-2">
              {audioPois.length} file{audioPois.length === 1 ? '' : 's'} available for conversion
            </p>
          </div>
          <button
            onClick={handleBatch48kConversion}
            disabled={isConverting48k || audioPois.length === 0}
            className="w-full md:w-auto inline-flex items-center justify-center px-5 py-3 rounded-lg border-2 border-slate-900 bg-slate-900 text-white font-semibold shadow-sm hover:bg-slate-700 hover:border-slate-700 disabled:bg-gray-300 disabled:text-gray-600 disabled:border-gray-300 disabled:cursor-not-allowed"
          >
            {isConverting48k ? 'Converting to 48k...' : 'Convert Existing Audio to 48k'}
          </button>
        </div>
      </div>
      )}

      {/* Language Selection */}
      <div className="rounded-lg border border-gray-200 p-4 space-y-4 mb-4">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">1</span>
          <h4 className="font-semibold text-gray-800">Voice & language settings</h4>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Audio Language</label>
            <select
              value={selectedLanguage}
              onChange={(e) => handleLanguageChange(e.target.value)}
              disabled={isGenerating}
              className="w-full p-2 border rounded bg-white"
            >
              {LANGUAGES.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Voice</label>
            <div className="relative">
              <Bus className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-500" />
              <select
                value={selectedVoice}
                onChange={(e) => handleVoiceChange(e.target.value)}
                disabled={isGenerating}
                className="w-full rounded border bg-white py-2 pl-9 pr-2"
              >
                {VOICE_GROUPS.map(group => (
                  <optgroup key={group.language} label={group.language}>
                    {group.voices.map(voiceId => {
                      const voice = AVAILABLE_VOICES[voiceId];
                      return (
                        <option key={voiceId} value={voiceId}>
                          {voice?.name || voiceId}
                        </option>
                      );
                    })}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Speech Rate - Always visible */}
        <div className="p-3 bg-blue-50 rounded border border-blue-200">
          <label className="block text-sm font-medium text-blue-900 mb-2">
            Speech Rate: {speechRate}x
          </label>
          <input
            type="range"
            min="0.5"
            max="2.0"
            step="0.05"
            value={speechRate}
            onChange={(e) => setSpeechRate(e.target.value)}
            disabled={isGenerating || isGeminiVoice()}
            className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <div className="flex justify-between text-xs text-gray-600 mt-1">
            <span>0.5x (Slower)</span>
            <span>1.0x (Normal)</span>
            <span>2.0x (Faster)</span>
          </div>
          {isGeminiVoice() ? (
            <div className="text-xs text-amber-700 mt-2 bg-amber-50 p-2 rounded border border-amber-300">
              <p className="font-medium mb-1">⚠️ Speech rate control is not available for Gemini voices</p>
              <p className="text-amber-600">
                To make Gemini speak faster, use the <strong>Pace slider</strong> in the purple "Gemini Voice Style Controls" section below. Set it to <strong>70-100</strong> for fast speech.
              </p>
            </div>
          ) : (
            <p className="text-xs text-gray-600 mt-2">
              Adjust the playback speed of the generated audio
            </p>
          )}
        </div>

        <div className="p-3 bg-emerald-50 rounded border border-emerald-200">
          <div className="flex items-center justify-between gap-4">
            <label className="flex items-center gap-2 text-sm font-medium text-emerald-900">
              <input
                type="checkbox"
                checked={chunkingEnabled}
                onChange={(e) => setChunkingEnabled(e.target.checked)}
                disabled={isGenerating}
                className="rounded border-emerald-300"
              />
              Enable chunking
            </label>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-emerald-900" htmlFor="batch-chunk-size">
                Chunk size
              </label>
              <input
                id="batch-chunk-size"
                type="number"
                min="100"
                step="50"
                value={chunkSize}
                onChange={(e) => setChunkSize(Number(e.target.value))}
                disabled={isGenerating || !chunkingEnabled}
                className="w-24 px-2 py-1 border border-emerald-300 rounded bg-white disabled:bg-gray-100"
              />
            </div>
          </div>
          <p className="text-xs text-emerald-700 mt-2">
            Turn this off to send the full narration in one request. Chunk size applies when chunking is enabled.
          </p>
        </div>

        <div className="rounded border border-slate-300 bg-slate-50">
          <button
            type="button"
            onClick={() => setIsChunkPreviewExpanded((prev) => !prev)}
            className="flex w-full items-center justify-between px-3 py-2 text-left"
          >
            <span className="text-xs font-medium text-slate-900">Inspect final text and chunks</span>
            {isChunkPreviewExpanded ? <ChevronUp className="h-4 w-4 text-slate-600" /> : <ChevronDown className="h-4 w-4 text-slate-600" />}
          </button>
          {isChunkPreviewExpanded && (
            <div className="border-t border-slate-200 px-3 py-3">
              {!batchChunkPreview ? (
                <p className="text-xs text-slate-600">Add at least one POI with narration text to preview the batch Gemini request.</p>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <label htmlFor="batch-preview-poi" className="text-xs font-medium text-slate-900">
                      Preview POI
                    </label>
                    <select
                      id="batch-preview-poi"
                      value={previewPoiName}
                      onChange={(e) => setPreviewPoiName(e.target.value)}
                      disabled={isGenerating}
                      className="rounded border border-slate-300 bg-white px-2 py-1 text-xs"
                    >
                      {describedPois.map((poi) => (
                        <option key={poi.name} value={poi.name}>
                          {poi.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 gap-2 text-xs text-slate-700 md:grid-cols-2">
                    <div className="rounded border border-slate-200 bg-white px-2 py-1.5">
                      <strong>Voice:</strong> {selectedVoice}
                    </div>
                    <div className="rounded border border-slate-200 bg-white px-2 py-1.5">
                      <strong>Chunking:</strong> {batchChunkPreview.shouldChunk ? `on (${batchChunkPreview.chunks.length} chunks)` : 'off'}
                    </div>
                    <div className="rounded border border-slate-200 bg-white px-2 py-1.5">
                      <strong>Chunk size:</strong> {batchChunkPreview.chunkSize}
                    </div>
                    <div className="rounded border border-slate-200 bg-white px-2 py-1.5">
                      <strong>SSML source:</strong> {batchChunkPreview.useSSML ? 'enabled before Gemini cleanup' : 'disabled'}
                    </div>
                  </div>

                  {batchChunkPreview.geminiPrompt && (
                    <div>
                      <p className="mb-1 text-xs font-medium text-slate-900">Gemini style prompt</p>
                      <pre className="whitespace-pre-wrap rounded border border-slate-200 bg-white p-2 text-xs text-slate-700">{batchChunkPreview.geminiPrompt}</pre>
                    </div>
                  )}

                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-900">Final text after cleanup/prefixing</p>
                    <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded border border-slate-200 bg-white p-2 text-xs text-slate-700">{batchChunkPreview.finalText}</pre>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-medium text-slate-900">Chunks that will be sent</p>
                    {batchChunkPreview.chunks.map((chunk, index) => (
                      <div key={`${batchChunkPreview.poiName}-chunk-${index}`} className="rounded border border-slate-200 bg-white">
                        <div className="border-b border-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                          Chunk {index + 1} · {chunk.length} chars
                        </div>
                        <pre className="max-h-48 overflow-auto whitespace-pre-wrap p-2 text-xs text-slate-700">{chunk}</pre>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Gemini Voice Style Controls - Only show for Gemini voices */}
        {isGeminiVoice() && (
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h3 className="text-sm font-semibold text-purple-900 mb-3">Gemini Voice Style Controls</h3>

            {/* Personality Preset Dropdown */}
            <div className="mb-4 p-3 bg-white rounded-lg border border-purple-300">
              <label className="block text-xs font-medium text-purple-900 mb-2">
                🎭 Voice Personality Preset
              </label>
              <select
                value={personalityPreset}
                onChange={(e) => handlePersonalityPresetChange(e.target.value)}
                disabled={isGenerating}
                className="w-full px-3 py-2 text-sm border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white disabled:bg-gray-100"
              >
                {Object.entries(personalityPresets).map(([key, preset]) => (
                  <option key={key} value={key}>
                    {preset.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-purple-700 mt-2">
                💡 Choose a preset personality or select Custom to fine-tune with sliders below
              </p>
            </div>

            {/* Tone Slider */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Tone: {geminiTone <= 30 ? 'Calm' : geminiTone >= 70 ? 'Excited' : 'Neutral'}
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={geminiTone}
                onChange={(e) => setGeminiTone(Number(e.target.value))}
                disabled={isGenerating}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-500"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Calm</span>
                <span>Neutral</span>
                <span>Excited</span>
              </div>
            </div>

            {/* Pace Slider */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Pace: {geminiPace <= 20 ? 'Very Slow' : geminiPace <= 40 ? 'Slow' : geminiPace <= 59 ? 'Normal' : geminiPace <= 79 ? 'Moderately Fast' : 'Very Fast'}
                <span className="ml-2 text-purple-600 font-normal">🎯 Controls speaking speed</span>
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={geminiPace}
                onChange={(e) => setGeminiPace(Number(e.target.value))}
                disabled={isGenerating}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-500"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Very Slow</span>
                <span>Normal</span>
                <span>Very Fast</span>
              </div>
              <p className="text-xs text-purple-700 mt-1 italic">
                💡 Ranges: 0-20=Very Slow, 21-40=Slow, 41-59=Normal, 60-79=Moderately Fast, 80-100=Very Fast
              </p>
            </div>

            {/* Formality Slider */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Formality: {geminiFormality <= 30 ? 'Casual' : geminiFormality >= 70 ? 'Formal' : 'Moderate'}
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={geminiFormality}
                onChange={(e) => setGeminiFormality(Number(e.target.value))}
                disabled={isGenerating}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-500"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Casual</span>
                <span>Moderate</span>
                <span>Formal</span>
              </div>
            </div>

            {/* Temperature Slider */}
            <div className="mb-4 p-4 bg-pink-200 rounded-lg border-2 border-pink-500">
              <label className="block text-xs font-medium text-pink-900 mb-2 font-bold">
                🌡️ TEMPERATURE: {geminiTemperature.toFixed(1)}
              </label>
              <input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={geminiTemperature}
                onChange={(e) => setGeminiTemperature(Number(e.target.value))}
                disabled={isGenerating}
                className="w-full h-2 bg-pink-300 rounded-lg appearance-none cursor-pointer accent-pink-600"
              />
              <div className="flex justify-between text-xs text-pink-800 mt-2 font-medium">
                <span>Deterministic (0.0)</span>
                <span>Balanced (1.0)</span>
                <span>Creative (2.0)</span>
              </div>
              <p className="text-xs text-pink-900 mt-2 italic font-medium">
                💡 Lower = consistent and stable, Higher = more expressive variation
              </p>
            </div>

            {/* Custom Prompt Input */}
            <div className="mb-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Custom Style Instructions (Optional)
              </label>
              <textarea
                value={geminiCustomPrompt}
                onChange={(e) => setGeminiCustomPrompt(e.target.value)}
                placeholder="e.g., 'Use a whisper', 'Speak with a British accent', 'Sound like a documentary narrator'"
                disabled={isGenerating}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                rows="2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Max 900 bytes. Natural language instructions for voice style.
              </p>
            </div>

            {/* Preview of generated prompt */}
            {buildGeminiStylePrompt() && (
              <div className="mt-3 p-2 bg-white rounded border border-purple-300">
                <p className="text-xs font-medium text-purple-900 mb-1">Generated Prompt:</p>
                <p className="text-xs text-gray-700 italic">{buildGeminiStylePrompt()}</p>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Generation Controls */}
      <div className="rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">2</span>
          <h4 className="font-semibold text-gray-800">Generate audio</h4>
        </div>
      <div className="flex gap-3">
        <button
          onClick={handleBatchGeneration}
          disabled={isGenerating || validPois.length === 0}
          className="flex-1 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-gray-400 flex items-center justify-center gap-2"
        >
          {isGenerating ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              Generating Audio...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Generate Missing Audio
            </>
          )}
        </button>

        <button
          onClick={() => {
            if (!window.confirm(`Re-generate audio for all ${describedPois.length} POIs with in-depth descriptions? Existing audio links will be replaced.`)) {
              return;
            }
            handleBatchGeneration({ forceRegenerate: true });
          }}
          disabled={isGenerating || describedPois.length === 0}
          className="flex-1 bg-amber-600 text-white px-4 py-2 rounded hover:bg-amber-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
          style={{ backgroundColor: '#d97706', color: '#ffffff', border: '1px solid #b45309' }}
        >
          {isGenerating ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              Re-doing Audio...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Re-do All Audio
            </>
          )}
        </button>

        <button
          onClick={() => setIsStopPickerOpen((prev) => !prev)}
          disabled={isGenerating || describedPois.length === 0}
          className="flex-1 bg-white text-amber-800 px-4 py-2 rounded border border-amber-600 hover:bg-amber-50 disabled:text-gray-400 disabled:border-gray-300 flex items-center justify-center gap-2"
        >
          <Layers className="w-4 h-4" />
          Re-do Selected Stops
          {isStopPickerOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {progress.length > 0 && (
          <button
            onClick={clearProgress}
            disabled={isGenerating}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Clear Log
          </button>
        )}
      </div>

      {isStopPickerOpen && (
        <div className="mt-3 rounded border border-amber-200 bg-amber-50">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 px-3 py-2">
            <span className="text-sm font-medium text-amber-900">
              {selectedPois.length} of {describedPois.length} stops selected
            </span>
            <div className="flex flex-wrap gap-2 text-xs">
              <button
                type="button"
                onClick={() => setSelectedPoiKeys(describedPois.map(getPoiKey))}
                disabled={isGenerating}
                className="rounded border border-amber-300 bg-white px-2 py-1 text-amber-900 hover:bg-amber-100"
              >
                All
              </button>
              <button
                type="button"
                onClick={selectStopsNotOnLatestModel}
                disabled={isGenerating}
                className="rounded border border-amber-300 bg-white px-2 py-1 text-amber-900 hover:bg-amber-100"
              >
                Not on latest model
              </button>
              <button
                type="button"
                onClick={() => setSelectedPoiKeys([])}
                disabled={isGenerating}
                className="rounded border border-amber-300 bg-white px-2 py-1 text-amber-900 hover:bg-amber-100"
              >
                None
              </button>
            </div>
          </div>

          <ul className="max-h-80 divide-y divide-amber-100 overflow-y-auto">
            {describedPois.map((poi) => {
              const poiKey = getPoiKey(poi);
              const info = getPoiAudioGenerationInfo(poi);
              return (
                <li key={poiKey}>
                  <label className="flex cursor-pointer items-start gap-3 px-3 py-2 hover:bg-amber-100/60">
                    <input
                      type="checkbox"
                      checked={selectedPoiKeys.includes(poiKey)}
                      onChange={() => togglePoiSelection(poiKey)}
                      disabled={isGenerating}
                      className="mt-1 rounded border-amber-400"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">{poi.name || 'Unnamed stop'}</p>
                      {info.hasAudio ? (
                        <p className="text-xs text-gray-600">
                          <span
                            className={`mr-2 inline-block rounded px-1.5 py-0.5 font-medium ${
                              info.isLatestModel ? 'bg-green-100 text-green-800' : 'bg-amber-200 text-amber-900'
                            }`}
                            title={info.modelIsRecorded ? 'Model recorded at generation time' : 'Inferred from the voice setting'}
                          >
                            {info.modelLabel}{info.modelIsRecorded ? '' : '?'}
                          </span>
                          {info.voiceLabel}
                          {' · '}
                          {info.generatedAt
                            ? `${formatGeneratedAt(info.generatedAt)}${info.dateSource === 'filename' ? ' (from file name)' : ''}`
                            : 'date unknown'}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500">No audio yet</p>
                      )}
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>

          <div className="flex items-center justify-between gap-3 border-t border-amber-200 px-3 py-2">
            <p className="text-xs text-amber-800">
              Uses the voice and settings above. A "?" means the model is inferred from the voice, not recorded.
            </p>
            <button
              type="button"
              onClick={() => {
                if (!window.confirm(`Re-generate audio for ${selectedPois.length} selected stop${selectedPois.length === 1 ? '' : 's'} with ${getVoiceName(selectedVoice)}? Existing audio links will be replaced.`)) {
                  return;
                }
                handleBatchGeneration({ pois: selectedPois });
              }}
              disabled={isGenerating || selectedPois.length === 0}
              className="shrink-0 rounded px-4 py-2 text-white disabled:bg-gray-400 flex items-center gap-2"
              style={selectedPois.length > 0 && !isGenerating ? { backgroundColor: '#d97706', border: '1px solid #b45309' } : undefined}
            >
              {isGenerating ? <Loader className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Re-do {selectedPois.length} Selected
            </button>
          </div>
        </div>
      )}
      </div>

      {false && conversionStats && (
        <div className="mb-4 p-3 bg-amber-50 rounded border border-amber-200">
          <h4 className="font-medium text-amber-800 mb-2">48k Conversion Statistics</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <span className="text-amber-700">Total POIs:</span>
              <span className="ml-2 font-medium">{conversionStats.total}</span>
            </div>
            <div>
              <span className="text-green-600">Successful:</span>
              <span className="ml-2 font-medium">{conversionStats.successful}</span>
            </div>
            <div>
              <span className="text-red-600">Failed:</span>
              <span className="ml-2 font-medium">{conversionStats.failed}</span>
            </div>
            <div>
              <span className="text-gray-600">Total Time:</span>
              <span className="ml-2 font-medium">{conversionStats.totalTime}s</span>
            </div>
          </div>
        </div>
      )}

      {/* Chunk Progress Indicator (shows during chunked processing) */}
      {chunkProgress && chunkProgress.total > 1 && isGenerating && (
        <div className="mb-4 p-3 bg-purple-50 rounded border border-purple-300">
          <div className="flex items-center gap-2 mb-2">
            <Layers className="w-4 h-4 text-purple-600" />
            <h4 className="font-medium text-purple-800">
              Chunked Processing Active
            </h4>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-purple-200 rounded-full h-2">
              <div
                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(chunkProgress.current / chunkProgress.total) * 100}%` }}
              />
            </div>
            <span className="text-sm font-medium text-purple-700">
              {chunkProgress.status === 'completed'
                ? `Done (${chunkProgress.total} chunks)`
                : `Chunk ${chunkProgress.current}/${chunkProgress.total}`
              }
            </span>
          </div>
          <p className="text-xs text-purple-600 mt-1">
            Long text is being processed in chunks to maintain audio quality
          </p>
        </div>
      )}

      {/* Statistics */}
      {generationStats && (
        <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200">
          <h4 className="font-medium text-blue-800 mb-2">Generation Statistics</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <span className="text-blue-600">Total POIs:</span>
              <span className="ml-2 font-medium">{generationStats.total}</span>
            </div>
            <div>
              <span className="text-green-600">Successful:</span>
              <span className="ml-2 font-medium">{generationStats.successful}</span>
            </div>
            <div>
              <span className="text-red-600">Failed:</span>
              <span className="ml-2 font-medium">{generationStats.failed}</span>
            </div>
            <div>
              <span className="text-gray-600">Total Time:</span>
              <span className="ml-2 font-medium">{generationStats.totalTime}s</span>
            </div>
          </div>
        </div>
      )}

      {/* Progress Log */}
      {(conversionProgress.length > 0 || progress.length > 0) && (
        <div className="border rounded">
          <div className="bg-gray-50 px-3 py-2 border-b flex items-center justify-between">
            <h4 className="font-medium text-gray-800">Generation Progress</h4>
            <span className="text-xs text-gray-500">
              {progress.length + conversionProgress.length} events
            </span>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {conversionProgress.map((item) => (
              <div
                key={item.id}
                className={`px-3 py-2 border-b last:border-b-0 flex items-start gap-2 ${
                  item.type === 'error' ? 'bg-red-50' :
                  item.type === 'success' ? 'bg-green-50' :
                  item.type === 'processing' ? 'bg-amber-50' : 'bg-white'
                }`}
              >
                {getProgressIcon(item.type)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800">{item.message}</p>
                  <p className="text-xs text-gray-500">{item.timestamp}</p>
                </div>
              </div>
            ))}
            {progress.map((item) => (
              <div
                key={item.id}
                className={`px-3 py-2 border-b last:border-b-0 flex items-start gap-2 ${
                  item.type === 'error' ? 'bg-red-50' : 
                  item.type === 'success' ? 'bg-green-50' : 
                  item.type === 'processing' ? 'bg-blue-50' : 'bg-white'
                }`}
              >
                {getProgressIcon(item.type)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800">{item.message}</p>
                  <p className="text-xs text-gray-500">{item.timestamp}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help Text */}
      {validPois.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <Volume2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          {describedPois.length > 0 ? (
            <>
              <p className="text-sm">All POIs with in-depth descriptions already have audio.</p>
              <p className="text-xs mt-1">This batch tool only generates audio for POIs that are missing it.</p>
            </>
          ) : (
            <>
              <p className="text-sm">No POIs with in-depth descriptions found.</p>
              <p className="text-xs mt-1">Add in-depth descriptions to POIs to enable audio generation.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BatchAudioGenerator;
